import { ref } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { isAxiosError } from 'axios'
import type { RacePriceTier, RacePricing, StoreRacePriceTierPayload } from '@/types/events'
import {
  listRacePriceTiers,
  createRacePriceTier,
  updateRacePriceTier,
  reorderRacePriceTiers,
  deleteRacePriceTier,
} from '@/api/racePriceTiers'
import { payoutNotReadyMessage } from '@/api/payouts'

export type TierMoveDirection = 'up' | 'down'

export interface RacePriceTierError {
  /** 422 field errors (name, price, ends_at, max_registrations, tiers, tier, tier_ids). */
  errors: Record<string, string[]>
  /** Server message of a payout_account_not_ready error, else null. */
  payoutNotReady: string | null
}

/** Map a failed tier request to inline field errors / the payout notice. */
export function parseRacePriceTierError(e: unknown): RacePriceTierError {
  const notReady = payoutNotReadyMessage(e)
  if (notReady !== null) {
    return { errors: {}, payoutNotReady: notReady }
  }
  if (isAxiosError<{ errors?: Record<string, string[]> }>(e) && e.response?.status === 422) {
    return { errors: e.response.data?.errors ?? {}, payoutNotReady: null }
  }
  return { errors: {}, payoutNotReady: null }
}

export function useRacePriceTiers(
  eventSlug: string,
  editionSlug: string,
  raceSlug: string,
): {
  tiers: Ref<RacePriceTier[]>
  pricing: Ref<RacePricing | null>
  loading: Ref<boolean>
  actionLoading: Ref<boolean>
  error: Ref<string | null>
  fetchTiers: () => Promise<void>
  createTier: (payload: StoreRacePriceTierPayload) => Promise<RacePriceTier>
  updateTier: (id: number, payload: StoreRacePriceTierPayload) => Promise<RacePriceTier>
  deleteTier: (id: number) => Promise<boolean>
  moveTier: (id: number, direction: TierMoveDirection) => Promise<boolean>
  setInitial: (tiers: RacePriceTier[], pricing: RacePricing | null) => void
} {
  const { t } = useI18n()

  const tiers = ref<RacePriceTier[]>([])
  const pricing = ref<RacePricing | null>(null)
  const loading = ref(false)
  const actionLoading = ref(false)
  const error = ref<string | null>(null)

  function setInitial(initialTiers: RacePriceTier[], initialPricing: RacePricing | null): void {
    tiers.value = [...initialTiers].sort((a, b) => a.position - b.position)
    pricing.value = initialPricing
  }

  async function fetchTiers(): Promise<void> {
    loading.value = true
    try {
      const result = await listRacePriceTiers(eventSlug, editionSlug, raceSlug)
      tiers.value = [...result.tiers].sort((a, b) => a.position - b.position)
      pricing.value = result.pricing
      error.value = null
    } catch {
      error.value = t('races.price_tiers.error_generic')
    } finally {
      loading.value = false
    }
  }

  /** First server message of a 422 (errors.tier, errors.tier_ids), else the generic message. */
  function mutationMessage(e: unknown): string {
    const { errors } = parseRacePriceTierError(e)
    const first = Object.values(errors)[0]?.[0]
    return first ?? t('races.price_tiers.error_generic')
  }

  // Create and update let the error propagate so the slide-over can show field errors inline.
  // They do not reload the list: the slide-over owns its own instance, the section refetches on `saved`.
  async function createTier(payload: StoreRacePriceTierPayload): Promise<RacePriceTier> {
    return createRacePriceTier(eventSlug, editionSlug, raceSlug, payload)
  }

  async function updateTier(id: number, payload: StoreRacePriceTierPayload): Promise<RacePriceTier> {
    return updateRacePriceTier(eventSlug, editionSlug, raceSlug, id, payload)
  }

  async function deleteTier(id: number): Promise<boolean> {
    actionLoading.value = true
    error.value = null
    try {
      await deleteRacePriceTier(eventSlug, editionSlug, raceSlug, id)
      await fetchTiers()
      return true
    } catch (e: unknown) {
      error.value = mutationMessage(e)
      return false
    } finally {
      actionLoading.value = false
    }
  }

  async function moveTier(id: number, direction: TierMoveDirection): Promise<boolean> {
    const index = tiers.value.findIndex((tier) => tier.id === id)
    const target = direction === 'up' ? index - 1 : index + 1
    if (index === -1 || target < 0 || target >= tiers.value.length) return false

    const ids = tiers.value.map((tier) => tier.id)
    const moved = ids[index]
    const swapped = ids[target]
    if (moved === undefined || swapped === undefined) return false
    ids[index] = swapped
    ids[target] = moved

    actionLoading.value = true
    error.value = null
    try {
      await reorderRacePriceTiers(eventSlug, editionSlug, raceSlug, ids)
      await fetchTiers()
      return true
    } catch (e: unknown) {
      const message = mutationMessage(e)
      // The list may be out of date (422 tier_ids): reload it, then show the message.
      await fetchTiers()
      error.value = message
      return false
    } finally {
      actionLoading.value = false
    }
  }

  return {
    tiers,
    pricing,
    loading,
    actionLoading,
    error,
    fetchTiers,
    createTier,
    updateTier,
    deleteTier,
    moveTier,
    setInitial,
  }
}
